import { autoPlacement, autoUpdate, FloatingPortal, offset, useFloating } from '@floating-ui/react'
import { useAtomValue } from 'jotai'
import { ReactNode, useCallback, useState } from 'react'
import { coverReferenceMiddleware } from '../../../utilities'
import { MetaTransformConfiguration, metaTransformDefault } from '../../../views'
import { editorFocusLockAtom, ValidFocusTarget } from '../../state'
import { Transformer } from '../transformer'
import { useSimulatorFocus } from '../use-simulator-focus'

export type EditableRenderArgs = {
	disabled: boolean
	isFocused: boolean
}

export type EditableOverlayArgs = {
	enableTransform: boolean
}

export type EditableToolbarArgs = {
	enableTransform: boolean
	setEnableTransform: (enableTransform: boolean) => void
	resetTransform: () => void // Reset the transform to the original value
}

export type EditableProps = {
	className?: string

	focusTargetId: ValidFocusTarget
	isGenerating: boolean

	transform: MetaTransformConfiguration
	onTransformChange: (transform: MetaTransformConfiguration) => void

	enableScaling?: boolean

	// Optional skeleton while generating. If not provided, a simple line skeleton is shown
	skeleton?: ReactNode

	// Render overlay that sits on top of the content (e.g., highlight)
	renderOverlay?: (args: EditableOverlayArgs) => ReactNode

	// Render toolbar floating near the content
	renderToolbar?: (args: EditableToolbarArgs) => ReactNode

	// Children is the editable underlay content (e.g., input/textarea)
	// Receives computed disabled and focus state
	children: (args: EditableRenderArgs) => ReactNode
}

export function Editable(props: EditableProps) {
	const { className, isGenerating, transform, onTransformChange, enableScaling = true } = props

	const [enableTransform, setEnableTransform] = useState(false)
	const focusLock = useAtomValue(editorFocusLockAtom)

	const { handleFocus, isFocused, mainRef, floatingRef } = useSimulatorFocus({
		focusTargetId: props.focusTargetId,
		disabled: props.isGenerating,
		lockFocus: enableTransform,
	})

	const { refs: overlayRefs, floatingStyles: overlayStyles, update: updateOverlay } = useFloating({
		open: isFocused,
		middleware: coverReferenceMiddleware(),
		whileElementsMounted: autoUpdate,
	})

	const { refs: toolbarRefs, floatingStyles: toolbarStyles, update: updateToolbar } = useFloating({
		open: isFocused,
		whileElementsMounted: autoUpdate,
		middleware: [
			autoPlacement({
				allowedPlacements: ['top', 'bottom'],
			}),
			offset(8),
		],
	})

	const handleUpdateTick = useCallback(() => {
		updateOverlay()
		updateToolbar()
	}, [updateOverlay, updateToolbar])

	const resetTransform = useCallback(() => {
		onTransformChange(metaTransformDefault)
		setEnableTransform(false)
	}, [onTransformChange, setEnableTransform])

	if (isGenerating) {
		return (
			<div className={className}>
				{props.skeleton ?? <div className="h-[1em] w-full min-w-10 bg-grey-200 rounded-full animate-pulse" />}
			</div>
		)
	}

	const disabled = focusLock && !isFocused

	return (
		<>
			<div className={className} onPointerDown={handleFocus} ref={mainRef}>
				<Transformer
					value={transform}
					onChange={onTransformChange}
					enableTransform={enableTransform}
					onUpdateTick={handleUpdateTick}
					enableScaling={enableScaling}
				>
					<div ref={toolbarRefs.setReference}>
						<div ref={overlayRefs.setReference}>
							{props.children({ disabled, isFocused })}
						</div>
					</div>
				</Transformer>
			</div>

			{isFocused && (
				<FloatingPortal>
					<div ref={floatingRef}>
						<div
							ref={overlayRefs.setFloating}
							style={overlayStyles}
							className="relative pointer-events-none"
						>
							{props.renderOverlay?.({ enableTransform })}
						</div>
						<div
							ref={toolbarRefs.setFloating}
							style={toolbarStyles}
							className="relative"
						>
							{props.renderToolbar?.({ enableTransform, setEnableTransform, resetTransform })}
						</div>
					</div>
				</FloatingPortal>
			)}
		</>
	)
}
